import { TenantSettings } from '../types';

interface InvoiceData {
    groupName?: string;
    memberName: string;
    memberEmail: string;
    billingDateTime: string;
    lineItems: Array<{
        date?: string;
        description: string;
        amount: string;
        tax: string;
        discount: string;
        total: string;
    }>;
    subtotal: string;
    tax: string;
    discount: string;
    total: string;
    paymentMethod: string;
    paymentProcessorId: string;
}

interface MemberInfo {
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
}

interface InvoiceOptions {
    invoiceData: InvoiceData;
    memberInfo?: MemberInfo;
    tenantSettings?: TenantSettings | null;
    transactionId?: string;
}

/**
 * Generates a formatted HTML invoice based on tenant settings and invoice data
 */
export const generateInvoiceHTML = ({ 
    invoiceData, 
    memberInfo, 
    tenantSettings,
    transactionId 
}: InvoiceOptions): string => {
    // Get member address if available - split into two lines
    const streetAddress = memberInfo?.address || '';
    const cityStateZip = memberInfo ? 
        [memberInfo.city, memberInfo.state, memberInfo.zipCode, memberInfo.country]
            .filter(Boolean)
            .join(', ') : '';

    // Get organization info from tenant settings
    const orgName = tenantSettings?.receipt_organization_name || 'Organization';
    const orgAddress = tenantSettings?.receipt_organization_address || '';
    const gstHstNumber = tenantSettings?.receipt_gst_hst_number || '';
    const headerColor = tenantSettings?.tenant_background_colour || '#3f51b5';
    const logoUrl = tenantSettings?.tenant_logo_img || '';

    // Generate invoice number from transaction ID if available
    const invoiceNumber = transactionId ? transactionId.substring(0, 8).toUpperCase() : 'N/A';

    return `
        <!DOCTYPE html>
        <html>
        <head>
            ${invoiceData?.paymentProcessorId ? `<title>Invoice - ${invoiceData.paymentProcessorId}</title>` : 
            '<title>Invoice</title>'}
            <style>
                body { 
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
                    max-width: 900px; 
                    margin: 20px auto; 
                    padding: 0;
                    color: #333;
                }
                .header { 
                    background-color: ${headerColor}; 
                    color: white; 
                    padding: 30px; 
                    margin-bottom: 30px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .header-left {
                    flex: 1;
                }
                .header h1 { 
                    margin: 0 0 10px 0; 
                    font-size: 2em;
                }
                .header-info {
                    font-size: 0.9em;
                    opacity: 0.95;
                    line-height: 1.6;
                }
                .header-logo {
                    max-height: 80px;
                    max-width: 200px;
                    object-fit: contain;
                }
                .content {
                    padding: 0 30px;
                }
                .invoice-details {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 30px;
                    gap: 40px;
                }
                .section {
                    flex: 1;
                }
                .section h3 {
                    color: ${headerColor};
                    margin: 0 0 10px 0;
                    font-size: 1.1em;
                    border-bottom: 2px solid ${headerColor};
                    padding-bottom: 5px;
                }
                .info-row { 
                    margin: 8px 0;
                    line-height: 1.6;
                }
                .info-row strong {
                    display: inline-block;
                    min-width: 120px;
                }
                table { 
                    width: 100%; 
                    border-collapse: collapse; 
                    margin: 30px 0;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }
                th, td { 
                    padding: 15px 12px; 
                    text-align: left; 
                    border-bottom: 1px solid #e0e0e0; 
                }
                th { 
                    background-color: ${headerColor}; 
                    color: white;
                    font-weight: 600;
                    text-transform: uppercase;
                    font-size: 0.85em;
                    letter-spacing: 0.5px;
                }
                tbody tr:hover {
                    background-color: #f5f5f5;
                }
                .totals { 
                    margin-top: 30px; 
                    text-align: right;
                    padding-right: 12px;
                }
                .totals-row { 
                    margin: 10px 0;
                    font-size: 1.05em;
                }
                .totals-row.subtotal {
                    padding-top: 10px;
                    border-top: 1px solid #e0e0e0;
                }
                .totals-row.total { 
                    font-size: 1.4em; 
                    font-weight: bold;
                    color: ${headerColor};
                    padding-top: 15px;
                    border-top: 2px solid ${headerColor};
                    margin-top: 15px;
                }
                .payment-info {
                    background-color: #f9f9f9;
                    padding: 20px;
                    margin-top: 40px;
                    border-radius: 5px;
                    border-left: 4px solid ${headerColor};
                }
                .payment-info h3 {
                    margin: 0 0 15px 0;
                    color: ${headerColor};
                }
                .footer {
                    margin-top: 50px;
                    padding: 20px 30px;
                    text-align: center;
                    color: #666;
                    font-size: 0.9em;
                    border-top: 2px solid #e0e0e0;
                }
                .no-print { 
                    text-align: center;
                    margin: 30px 0;
                }
                .no-print button {
                    background-color: ${headerColor};
                    color: white;
                    border: none;
                    padding: 12px 30px;
                    font-size: 1em;
                    border-radius: 5px;
                    cursor: pointer;
                    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
                }
                .no-print button:hover {
                    opacity: 0.9;
                }
                @media print { 
                    .no-print { display: none; }
                    body { margin: 0; }
                    .header { margin-bottom: 20px; }
                }
            </style>
        </head>
        <body>
            <div class="header">
                <div class="header-left">
                    <h1>INVOICE</h1>
                    <div class="header-info">
                        <strong>${orgName}</strong><br>
                        ${orgAddress ? orgAddress.replace(/\n/g, '<br>') : ''}
                        ${gstHstNumber ? `<br>GST/HST #: ${gstHstNumber}` : ''}
                    </div>
                </div>
                ${logoUrl ? `<img src="${logoUrl}" alt="Organization Logo" class="header-logo">` : ''}
            </div>
            
            <div class="content">
                <div class="invoice-details">
                    <div class="section">
                        <h3>Bill To</h3>
                        <div class="info-row"><strong>Member:</strong> ${invoiceData.memberName}</div>
                        <div class="info-row"><strong>Email:</strong> ${invoiceData.memberEmail}</div>
                        ${streetAddress || cityStateZip ? `<div class="info-row"><strong>Address:</strong>${streetAddress}${cityStateZip}</div>` :  ''}
                    </div>
                    
                    <div class="section">
                        <h3>Invoice Details</h3>
                        <div class="info-row"><strong>Date:</strong> ${invoiceData.billingDateTime}</div>
                        <div class="info-row"><strong>Invoice #:</strong> ${invoiceNumber}</div>
                    </div>
                </div>

                <h3 style="color: ${headerColor}; margin: 30px 0 15px 0;">Line Items</h3>
                <table>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Description</th>
                            <th style="text-align: right;">Amount</th>
                            <th style="text-align: right;">Tax</th>
                            <th style="text-align: right;">Discount</th>
                            <th style="text-align: right;">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${invoiceData.lineItems.map((item) => `
                            <tr>
                                <td>${item.date || ''}</td>
                                <td>${item.description}</td>
                                <td style="text-align: right;">$${item.amount}</td>
                                <td style="text-align: right;">$${item.tax}</td>
                                <td style="text-align: right;">${item.discount !== '0.00' ? '-$' + item.discount : '-'}</td>
                                <td style="text-align: right;"><strong>$${item.total}</strong></td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                
                <div class="totals">
                    <div class="totals-row subtotal">Subtotal: $${invoiceData.subtotal}</div>
                    <div class="totals-row">Tax: $${invoiceData.tax}</div>
                    ${invoiceData.discount !== '0.00' ? `<div class="totals-row">Discount: -$${invoiceData.discount}</div>` : ''}
                    <div class="totals-row total">Total Charged: $${invoiceData.total}</div>
                </div>

                <div class="payment-info">
                    <h3>Payment Information</h3>
                    <div class="info-row"><strong>Payment Method:</strong> ${invoiceData.paymentMethod}</div>
                    <div class="info-row"><strong>Transaction ID:</strong> ${invoiceData.paymentProcessorId}</div>
                </div>
            </div>

            <div class="footer">
                <p>Thank you for your business!</p>
                ${orgName ? `<p>${orgName}</p>` : ''}
            </div>

            <div class="no-print">
                <button onclick="window.print()">Print Invoice</button>
            </div>
        </body>
        </html>
    `;
};

/**
 * Opens a new window with the generated invoice HTML
 */
export const openInvoiceWindow = (invoiceHTML: string): boolean => {
    const newWindow = window.open('', '_blank');
    if (newWindow) {
        newWindow.document.write(invoiceHTML);
        newWindow.document.close();
        return true;
    }
    return false;
};
